import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import Breadcrumb from '../components/Breadcrumb';
import { 
  Users, 
  Search, 
  Filter, 
  RefreshCw, 
  Eye, 
  ChevronLeft, 
  ChevronRight,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  UserCheck,
  FileCheck,
  Send,
  Truck,
  XCircle,
  ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  new_application: { label: 'New Application', color: 'bg-blue-100 text-blue-800', icon: FileText },
  new: { label: 'New Application', color: 'bg-blue-100 text-blue-800', icon: FileText },
  reviewed: { label: 'Reviewed', color: 'bg-purple-100 text-purple-800', icon: Eye },
  approved_pending_docs: { label: 'Approved – Pending Docs', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  docs_submitted: { label: 'Docs Submitted', color: 'bg-orange-100 text-orange-800', icon: FileCheck },
  docs_verified: { label: 'Docs Verified', color: 'bg-teal-100 text-teal-800', icon: CheckCircle },
  contract_sent: { label: 'Contract Sent', color: 'bg-indigo-100 text-indigo-800', icon: Send },
  contract_executed: { label: 'Contract Executed', color: 'bg-cyan-100 text-cyan-800', icon: FileText },
  active_dispatch_eligible: { label: 'Active – Dispatch Eligible', color: 'bg-green-100 text-green-800', icon: Truck },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: XCircle }
};

// Statuses to show in KPI cards (excluding 'new' duplicate)
const KPI_STATUSES = [
  'new_application',
  'reviewed', 
  'approved_pending_docs',
  'docs_submitted',
  'docs_verified',
  'contract_sent',
  'contract_executed',
  'active_dispatch_eligible',
  'rejected'
];

const ApplicantTrackingDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const limit = 25;

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [driverTypeFilter, setDriverTypeFilter] = useState('');
  const [laneFilter, setLaneFilter] = useState('');
  const [eligibilityFilter, setEligibilityFilter] = useState('');
  const [autoRejectFilter, setAutoRejectFilter] = useState('');
  const [fromDashboard, setFromDashboard] = useState(false);

  // Status counts
  const [statusCounts, setStatusCounts] = useState({});

  const allowedRoles = ['admin', 'super_admin', 'dispatcher', 'safety_compliance_admin', 'ops_admin'];

  useEffect(() => {
    if (user && !allowedRoles.includes(user.role)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Read URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusParam = params.get('status');
    const categoryParam = params.get('category');
    
    if (statusParam) {
      setStatusFilter(statusParam);
      setFromDashboard(true);
    }
    
    // Handle category filter from dashboard KPIs
    if (categoryParam === 'applicants') {
      // Show all applicants (no specific status filter needed)
      setFromDashboard(true);
    }
  }, [location.search]);

  const fetchApplications = async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch all applications to calculate status counts
      const allResponse = await fetch(`${API_URL}/api/onboarding/queue?limit=500`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!allResponse.ok) throw new Error('Failed to fetch applications');
      
      const allData = await allResponse.json();
      const allApps = allData.applications || [];
      
      // Calculate status counts (combine 'new' and 'new_application')
      const counts = {};
      KPI_STATUSES.forEach(status => {
        if (status === 'new_application') {
          // Count both 'new' and 'new_application'
          counts[status] = allApps.filter(app => app.status === 'new' || app.status === 'new_application').length;
        } else {
          counts[status] = allApps.filter(app => app.status === status).length;
        }
      });
      setStatusCounts(counts);
      
      // Apply filters
      let filtered = [...allApps];
      
      if (statusFilter) {
        if (statusFilter === 'new_application') {
          // Filter both 'new' and 'new_application'
          filtered = filtered.filter(app => app.status === 'new' || app.status === 'new_application');
        } else {
          filtered = filtered.filter(app => app.status === statusFilter);
        }
      }
      
      if (driverTypeFilter) {
        filtered = filtered.filter(app => app.driver_type === driverTypeFilter);
      }
      
      if (laneFilter) {
        filtered = filtered.filter(app => app.preferred_lane === laneFilter);
      }
      
      if (eligibilityFilter) {
        filtered = filtered.filter(app => (app.eligibility_status || 'UNKNOWN') === eligibilityFilter);
      }
      
      if (autoRejectFilter === 'auto') {
        filtered = filtered.filter(app => app.auto_rejected === true);
      } else if (autoRejectFilter === 'manual') {
        filtered = filtered.filter(app => !app.auto_rejected);
      }
      
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        filtered = filtered.filter(app => 
          `${app.first_name} ${app.last_name}`.toLowerCase().includes(search) ||
          app.email.toLowerCase().includes(search) ||
          app.phone.includes(search)
        );
      }
      
      // Sort by updated_at descending
      filtered.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
      
      setTotal(filtered.length);
      
      // Paginate
      const startIdx = page * limit;
      setApplications(filtered.slice(startIdx, startIdx + limit));
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchApplications();
    }
  }, [token, page, statusFilter, driverTypeFilter, laneFilter, eligibilityFilter, autoRejectFilter, searchTerm]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [statusFilter, driverTypeFilter, laneFilter, eligibilityFilter, autoRejectFilter, searchTerm]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDriverType = (type) => {
    const types = {
      'lease_on': 'Lease-On',
      'owner_operator': 'Owner-Operator',
      'dispatch_only': 'Dispatch-Only',
      'company_driver': 'Company Driver'
    };
    return types[type] || type || '-';
  };

  const formatLane = (lane) => {
    const lanes = {
      'otr': 'OTR',
      'regional': 'Regional',
      'dedicated': 'Dedicated',
      'any': 'Any'
    };
    return lanes[lane] || lane || '-';
  };

  const formatExperience = (exp) => {
    const experiences = {
      'less_than_1': '< 1 yr',
      '1_2': '1-2 yrs',
      '2_5': '2-5 yrs',
      '5_10': '5-10 yrs',
      '10_plus': '10+ yrs'
    };
    return experiences[exp] || exp || '-';
  };

  const totalApplicants = Object.values(statusCounts).reduce((a, b) => a + b, 0);

  if (!user || !allowedRoles.includes(user.role)) {
    return null;
  }

  return (
    <DashboardLayout>
      <div className="p-6" data-testid="applicant-tracking-dashboard">
        {/* Breadcrumb Navigation */}
        <Breadcrumb 
          items={[
            { label: 'Applicants' }
          ]}
        />

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-900" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Applicant Tracking</h1>
                <p className="text-sm text-gray-500">
                  {statusFilter && STATUS_CONFIG[statusFilter] 
                    ? `Filtered: ${STATUS_CONFIG[statusFilter].label} (${statusCounts[statusFilter] || 0})` 
                    : `${totalApplicants} total applicants`}
                </p>
              </div>
            </div>
            <button
              onClick={fetchApplications}
              disabled={loading}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Status KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          {KPI_STATUSES.map((status) => {
            const config = STATUS_CONFIG[status];
            const IconComponent = config.icon;
            const count = statusCounts[status] || 0;
            const isActive = statusFilter === status;
            
            return (
              <button
                key={status}
                onClick={() => setStatusFilter(isActive ? '' : status)}
                data-testid={`status-card-${status}`}
                className={`p-4 rounded-xl border transition-all text-left ${
                  isActive 
                    ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${config.color.split(' ')[0]}`}>
                    <IconComponent className={`w-4 h-4 ${config.color.split(' ')[1]}`} />
                  </div>
                  <span className="text-2xl font-bold text-gray-900">{count}</span>
                </div>
                <p className="text-xs text-gray-600 truncate">{config.label}</p>
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-input"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="status-filter"
                className="pl-4 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 appearance-none bg-white min-w-[180px]"
              >
                <option value="">All Statuses</option>
                {KPI_STATUSES.map((status) => (
                  <option key={status} value={status}>{STATUS_CONFIG[status].label}</option>
                ))}
              </select>
            </div>

            {/* Driver Type Filter */}
            <div className="relative">
              <select
                value={driverTypeFilter}
                onChange={(e) => setDriverTypeFilter(e.target.value)}
                data-testid="driver-type-filter"
                className="pl-4 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 appearance-none bg-white min-w-[160px]"
              >
                <option value="">All Driver Types</option>
                <option value="lease_on">Lease-On</option>
                <option value="owner_operator">Owner-Operator</option>
                <option value="dispatch_only">Dispatch-Only</option>
                <option value="company_driver">Company Driver</option>
              </select>
            </div>

            {/* Lane Filter */}
            <div className="relative">
              <select
                value={laneFilter}
                onChange={(e) => setLaneFilter(e.target.value)}
                data-testid="lane-filter"
                className="pl-4 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 appearance-none bg-white min-w-[140px]"
              >
                <option value="">All Lanes</option>
                <option value="otr">OTR</option>
                <option value="regional">Regional</option>
                <option value="dedicated">Dedicated</option>
                <option value="any">Any</option>
              </select>
            </div>

            {/* Eligibility Filter */}
            <div className="relative">
              <select
                value={eligibilityFilter}
                onChange={(e) => setEligibilityFilter(e.target.value)}
                data-testid="eligibility-filter"
                className="pl-4 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 appearance-none bg-white min-w-[150px]"
              >
                <option value="">All Eligibility</option>
                <option value="ELIGIBLE">Eligible</option>
                <option value="INELIGIBLE">Ineligible</option>
                <option value="UNKNOWN">Unknown</option>
              </select>
            </div>

            {/* Auto-Reject Filter */}
            <div className="relative">
              <select
                value={autoRejectFilter}
                onChange={(e) => setAutoRejectFilter(e.target.value)}
                data-testid="auto-reject-filter"
                className="pl-4 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 appearance-none bg-white min-w-[150px]"
              >
                <option value="">All Applications</option>
                <option value="auto">Auto-Rejected</option>
                <option value="manual">Not Auto-Rejected</option>
              </select>
            </div>

            {/* Clear Filters */}
            {(statusFilter || driverTypeFilter || laneFilter || eligibilityFilter || autoRejectFilter || searchTerm) && (
              <button
                onClick={() => {
                  setStatusFilter('');
                  setDriverTypeFilter('');
                  setLaneFilter('');
                  setEligibilityFilter('');
                  setAutoRejectFilter('');
                  setSearchTerm('');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 text-sm"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading && applications.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
              Loading applicants...
            </div>
          ) : applications.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <UserCheck className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No applicants found</p>
              <p className="text-sm mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full" data-testid="applicants-table">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Phone</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Driver Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Lane</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Years Exp</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Eligibility</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Last Updated</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {applications.map((app) => {
                    const statusConfig = STATUS_CONFIG[app.status] || { label: app.status, color: 'bg-gray-100 text-gray-800' };
                    
                    return (
                      <tr key={app.id} className="hover:bg-gray-50" data-testid={`applicant-row-${app.id}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">
                            {app.first_name} {app.last_name}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{app.phone || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{app.email || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatDriverType(app.driver_type)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatLane(app.preferred_lane)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatExperience(app.years_experience)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
                            {statusConfig.label}
                          </span>
                        </td>
                        <td className="px-4 py-3" data-testid={`eligibility-cell-${app.id}`}>
                          {(() => {
                            const elig = app.eligibility_status || 'UNKNOWN';
                            const eligColor = elig === 'ELIGIBLE'
                              ? 'bg-green-100 text-green-800'
                              : elig === 'INELIGIBLE'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-gray-100 text-gray-600';
                            const eligLabel = elig.charAt(0) + elig.slice(1).toLowerCase();
                            return (
                              <div className="flex flex-col gap-1">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${eligColor}`} title={app.eligibility_reason || ''}>
                                  {eligLabel}
                                </span>
                                {app.auto_rejected && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-testid={`auto-rejected-badge-${app.id}`} title={app.auto_rejected_reason || 'Auto-rejected'}>
                                    Auto-Rejected
                                  </span>
                                )}
                                {app.auto_rejected && app.rejection_email_status && app.rejection_email_status !== 'NONE' && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700" data-testid={`rejection-email-status-${app.id}`} title="Rejection email status">
                                    Email: {app.rejection_email_status}
                                  </span>
                                )}
                                {app.screening_status && app.screening_status !== 'NOT_EVALUATED' && (() => {
                                  const s = app.screening_status;
                                  const scrColor = s === 'PASSED'
                                    ? 'bg-green-100 text-green-800'
                                    : s === 'FAILED'
                                      ? 'bg-red-100 text-red-800'
                                      : s === 'NEEDS_REVIEW'
                                        ? 'bg-amber-100 text-amber-800'
                                        : 'bg-gray-100 text-gray-600';
                                  const scrLabel = s.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
                                  return (
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${scrColor}`} data-testid={`screening-status-${app.id}`} title="Screening status">
                                      Screening: {scrLabel}
                                    </span>
                                  );
                                })()}
                                {app.workflow_status && app.workflow_status !== 'NOT_ROUTED' && (() => {
                                  const w = app.workflow_status;
                                  const wfColor = w === 'READY_FOR_RECRUITER'
                                    ? 'bg-blue-100 text-blue-800'
                                    : w === 'AUTO_REJECTED'
                                      ? 'bg-red-100 text-red-800'
                                      : w === 'MANUAL_REVIEW'
                                        ? 'bg-amber-100 text-amber-800'
                                        : 'bg-indigo-100 text-indigo-800';
                                  const wfLabel = w.split('_').map(x => x.charAt(0) + x.slice(1).toLowerCase()).join(' ');
                                  return (
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${wfColor}`} data-testid={`workflow-status-${app.id}`} title={app.workflow_reason || 'Workflow placement'}>
                                      Workflow: {wfLabel}
                                    </span>
                                  );
                                })()}
                                {app.talent_pool_status && app.talent_pool_status !== 'NOT_EVALUATED' && app.talent_pool && app.talent_pool !== 'NONE' && (() => {
                                  const p = app.talent_pool;
                                  const tpColor = p === 'ACTIVE_POOL'
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : p === 'REJECTED_POOL' || p === 'NOT_ELIGIBLE'
                                      ? 'bg-red-100 text-red-800'
                                      : p === 'REVIEW_POOL'
                                        ? 'bg-amber-100 text-amber-800'
                                        : 'bg-purple-100 text-purple-800';
                                  const tpLabel = p.split('_').map(x => x.charAt(0) + x.slice(1).toLowerCase()).join(' ');
                                  return (
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${tpColor}`} data-testid={`talent-pool-${app.id}`} title={app.talent_pool_reason || 'Talent pool'}>
                                      Pool: {tpLabel}
                                    </span>
                                  );
                                })()}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">{formatDate(app.updated_at)}</td>
                        <td className="px-4 py-3">
                          <Link
                            to={`/onboarding/${app.id}`}
                            data-testid={`view-btn-${app.id}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-900 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Mobile card list (preserves desktop table above) */}
          {!loading && applications.length > 0 && (
            <div className="md:hidden divide-y divide-gray-200" data-testid="applicants-cards">
              {applications.map((app) => {
                const statusConfig = STATUS_CONFIG[app.status] || { label: app.status, color: 'bg-gray-100 text-gray-800' };
                const elig = app.eligibility_status || 'UNKNOWN';
                const eligColor = elig === 'ELIGIBLE'
                  ? 'bg-green-100 text-green-800'
                  : elig === 'INELIGIBLE'
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-gray-100 text-gray-600';
                return (
                  <div key={app.id} className="p-4" data-testid={`applicant-card-${app.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 break-words">{app.first_name} {app.last_name}</div>
                        <div className="text-sm text-gray-500 break-all">{app.email || '-'}</div>
                        <div className="text-sm text-gray-500 break-all">{app.phone || '-'}</div>
                      </div>
                      <span className={`shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
                        {statusConfig.label}
                      </span>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-600">
                      <div><span className="text-gray-400">Type:</span> {formatDriverType(app.driver_type)}</div>
                      <div><span className="text-gray-400">Lane:</span> {formatLane(app.preferred_lane)}</div>
                      <div><span className="text-gray-400">Exp:</span> {formatExperience(app.years_experience)}</div>
                      <div><span className="text-gray-400">Updated:</span> {formatDate(app.updated_at)}</div>
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${eligColor}`} title={app.eligibility_reason || ''}>
                        {elig.charAt(0) + elig.slice(1).toLowerCase()}
                      </span>
                      <Link
                        to={`/onboarding/${app.id}`}
                        data-testid={`view-btn-mobile-${app.id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-900 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {total > limit && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50">
              <div className="text-sm text-gray-500">
                Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total} applicants
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={(page + 1) * limit >= total}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ApplicantTrackingDashboard;
